import PermissionButton from '../../permissions/PermissionButton';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Descriptions,
  message,
  Popconfirm,
  Drawer,
  Input,
  Select,
  Spin,
  Modal,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  RollbackOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

import AddItemToCorrectionStockForm from './AddItemToCorrectionStockForm';
import NestedAchatTable from '../achat/NestedAchatTable';
import ExcelDetailImport from '../shared/ExcelDetailImport';
import {
  useFetchCorrectionStock,
  useUpdateCorrectionStock,
  useDeleteCorrectionStock,
  useValidateCorrectionStock,
  useUnvalidateCorrectionStock,
} from '../../hooks/avoirHooks';
import { useFetchAllEntrepots } from '../../hooks/entrepotHooks';
import EditableDateField from '../../utils/EditableDateField';

const normalizeDetails = (details) => (
  (details || []).map((detail) => {
    const qty = Number(detail.QTE) || 0;
    return {
      CODE_ART: detail.CODE_ART,
      ARTICLE: detail.ARTICLE || detail.article?.ARTICLE || '',
      QTE: Math.abs(qty),
      REMARQUE: detail.REMARQUE || '',
    };
  })
);

const getCorrectionType = (details = [], headerType) => {
  if (['ENTREE', 'SORTIE'].includes(headerType)) return headerType;
  const total = details.reduce((sum, detail) => sum + (Number(detail.QTE) || 0), 0);
  return total < 0 ? 'SORTIE' : 'ENTREE';
};

const SingleCorrectionStock = ({ refCor, onBack, onDeleted }) => {
  const { data: initialCorrection, loading, error, refetch } = useFetchCorrectionStock(refCor);
  const { handleUpdate, loading: updating } = useUpdateCorrectionStock();
  const { handleDelete, loading: deleting } = useDeleteCorrectionStock();
  const { validate, loading: validating } = useValidateCorrectionStock();
  const { unvalidate, loading: unvalidating } = useUnvalidateCorrectionStock();
  const { entrepots, loading: loadingEntrepots } = useFetchAllEntrepots();

  const [localCorrection, setLocalCorrection] = useState(null);
  const [localItems, setLocalItems] = useState(null);
  const [correctionType, setCorrectionType] = useState('ENTREE');
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [isAddItemDrawerVisible, setIsAddItemDrawerVisible] = useState(false);

  const isValidated = Boolean(localCorrection?.VALIDER);

  useEffect(() => {
    if (initialCorrection) {
      setLocalCorrection({ ...initialCorrection });
      setLocalItems(normalizeDetails(initialCorrection.details || []));
      setCorrectionType(getCorrectionType(initialCorrection.details, initialCorrection.TYPE));
    }
  }, [initialCorrection]);

  const entrepotOptions = useMemo(
    () => (entrepots || []).map((ent) => ({ value: ent.code_ent, label: `${ent.code_ent} - ${ent.name}` })),
    [entrepots]
  );

  const handleFieldChange = (field, value) => {
    setLocalCorrection((prev) => ({
      ...prev,
      [field]: value,
    }));
    setHasLocalChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!refCor) return;
    try {
      await handleUpdate(refCor, {
        CODE_ENT: localCorrection.CODE_ENT,
        TYPE: correctionType,
        REMARQUE: localCorrection.REMARQUE || undefined,
        DATE_COR: localCorrection.DATE_COR || undefined,
        details: localItems.map((item) => ({
          CODE_ART: item.CODE_ART,
          ARTICLE: item.ARTICLE,
          QTE: Math.abs(Number(item.QTE)) * (correctionType === 'SORTIE' ? -1 : 1),
          REMARQUE: item.REMARQUE || undefined,
        })),
      });
      setHasLocalChanges(false);
      message.success('Correction de stock enregistrée');
      await refetch();
    } catch (e) {
      message.error(e?.message || "Erreur lors de l'enregistrement");
    }
  };

  const handleSave = (row) => {
    const normalizedRow = {
      ...row,
      QTE: Math.abs(Number(row.QTE) || 0),
    };
    setLocalItems((prevItems) =>
      prevItems.map((item) =>
        item.CODE_ART === normalizedRow.CODE_ART ? { ...item, ...normalizedRow } : item
      )
    );
    setHasLocalChanges(true);
  };

  const handleDeleteItem = (codeArt) => {
    setLocalItems((prevItems) => prevItems.filter((item) => item.CODE_ART !== codeArt));
    setHasLocalChanges(true);
    message.success("Article supprimé localement. Cliquez sur 'Enregistrer' pour sauvegarder.");
  };

  const handleExcelImport = (importedItems) => {
    let addedCount = 0;
    let updatedCount = 0;
    setLocalItems((prevItems) => {
      const updated = [...(prevItems || [])];
      importedItems.forEach((newItem) => {
        const existingIndex = updated.findIndex((item) => item.CODE_ART === newItem.CODE_ART);
        if (existingIndex >= 0) {
          updated[existingIndex] = {
            ...updated[existingIndex],
            QTE: newItem.QTE,
            REMARQUE: newItem.REMARQUE || updated[existingIndex].REMARQUE,
          };
          updatedCount += 1;
        } else {
          updated.push(newItem);
          addedCount += 1;
        }
      });
      return updated;
    });
    setHasLocalChanges(true);
    message.success(
      `${addedCount} article(s) ajouté(s), ${updatedCount} mis à jour. Cliquez sur 'Enregistrer Modif.' pour sauvegarder.`
    );
  };

  const handleDeleteCorrection = async () => {
    if (!refCor) return;
    try {
      await handleDelete(refCor);
      message.success('Correction de stock supprimée');
      if (onDeleted) {
        onDeleted();
      } else if (onBack) {
        onBack();
      }
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la suppression');
    }
  };

  const handleValidate = async () => {
    if (hasLocalChanges) {
      message.warning('Enregistrez d\'abord les modifications avant de valider');
      return;
    }
    try {
      await validate(refCor);
      message.success('Correction de stock validée');
      if (onBack) {
        onBack();
      }
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la validation');
    }
  };

  const handleUnvalidate = async () => {
    try {
      await unvalidate(refCor);
      message.success('Correction de stock dévalidée');
      await refetch();
    } catch (e) {
      message.error(e?.message || 'Erreur lors de la dévalidation');
    }
  };

  if (error) {
    return (
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Retour</Button>
          <span style={{ color: 'red' }}>{error}</span>
        </Space>
      </Card>
    );
  }

  return (
    <Spin spinning={loading}>
      <Card
        title={(
          <>
            Correction Stock - {localCorrection?.REF_COR || ''}
            {isValidated ? (
              <Tag color="green" style={{ marginLeft: "8px" }}>
                Validée
              </Tag>
            ) : (
              <Tag color="orange" style={{ marginLeft: "8px" }}>
                Enregistrée
              </Tag>
            )}
          </>
        )}
        bordered={false}
        extra={(
          <Space>
            {hasLocalChanges && (
              <PermissionButton
                icon={<UndoOutlined />}
                onClick={() => {
                  Modal.confirm({
                    title: 'Annuler les modifications',
                    content: 'Êtes-vous sûr de vouloir annuler toutes les modifications?',
                    okText: 'Oui, Annuler tout',
                    cancelText: 'Non, Garder les modifications',
                    onOk: () => {
                      setLocalCorrection({ ...initialCorrection });
                      setLocalItems(normalizeDetails(initialCorrection?.details || []));
                      setCorrectionType(getCorrectionType(initialCorrection?.details, initialCorrection?.TYPE));
                      setHasLocalChanges(false);
                      message.success('Toutes les modifications ont été annulées');
                    }
                  });
                }}
              >
                Annuler modif.
              </PermissionButton>
            )}
            {!isValidated && (
              <PermissionButton permissionModule="stockCorrections" permissionAction="validate"
                type="primary"
                onClick={handleValidate}
                disabled={hasLocalChanges}
                loading={validating}
              >
                Valider
              </PermissionButton>
            )}
            {isValidated && (
              <PermissionButton permissionModule="stockCorrections" permissionAction="unvalidate"
                icon={<RollbackOutlined />}
                onClick={handleUnvalidate}
                loading={unvalidating}
              >
                Dévalider
              </PermissionButton>
            )}
            <PermissionButton permissionModule="stockCorrections" permissionAction="update"
              type="primary"
              onClick={handleSaveChanges}
              disabled={!hasLocalChanges}
              loading={updating}
            >
              Enregistrer Modif.
            </PermissionButton>
            <Popconfirm
              title="Supprimer cette correction de stock ?"
              onConfirm={handleDeleteCorrection}
            >
              <PermissionButton permissionModule="stockCorrections" permissionAction="delete" danger icon={<DeleteOutlined />} loading={deleting}>
                Supprimer
              </PermissionButton>
            </Popconfirm>
            <Button
              type="default"
              onClick={onBack}
              style={{
                backgroundColor: 'white',
                color: '#EE2625',
                border: '1px solid #EE2625',
              }}
            >
              SORTIR
            </Button>
          </Space>
        )}
        style={{ position: 'relative' }}
      >
        <Descriptions
          bordered
          column={{
            xs: 1,
            sm: 2,
            md: 3,
          }}
          size="small"
          labelStyle={{ fontWeight: 'bold', textAlign: 'right' }}
          contentStyle={{ minWidth: '150px' }}
        >
          <Descriptions.Item label="Référence">{localCorrection?.REF_COR}</Descriptions.Item>
          <Descriptions.Item label="Entrepôt">
            {isValidated ? (
              localCorrection?.entrepot?.name
                ? `${localCorrection.entrepot.code_ent} - ${localCorrection.entrepot.name}`
                : localCorrection?.CODE_ENT
            ) : (
              <Select
                showSearch
                placeholder="Entrepôt"
                options={entrepotOptions}
                value={localCorrection?.CODE_ENT}
                loading={loadingEntrepots}
                onChange={(value) => handleFieldChange('CODE_ENT', value)}
                style={{ width: '100%' }}
                disabled={isValidated}
              />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Date">
            <EditableDateField
              value={localCorrection?.DATE_COR}
              onChange={(newDate) => handleFieldChange('DATE_COR', newDate)}
              disabled={isValidated}
              withTime
            />
          </Descriptions.Item>
          <Descriptions.Item label="Type">
            <Select
              value={correctionType}
              options={[
                { value: 'ENTREE', label: 'Entrée' },
                { value: 'SORTIE', label: 'Sortie' },
              ]}
              onChange={(value) => {
                setCorrectionType(value);
                setHasLocalChanges(true);
              }}
              disabled={isValidated}
              style={{ width: '100%' }}
            />
          </Descriptions.Item>
          <Descriptions.Item label="Remarque" span={2}>
            {isValidated ? (
              localCorrection?.REMARQUE || '—'
            ) : (
              <Input.TextArea
                value={localCorrection?.REMARQUE || ''}
                onChange={(e) => handleFieldChange('REMARQUE', e.target.value)}
                autoSize={{ minRows: 1, maxRows: 4 }}
              />
            )}
          </Descriptions.Item>
          {localCorrection?.DATEVALID && (
            <Descriptions.Item label="Validée le" span={2}>
              {dayjs(localCorrection.DATEVALID).format('DD/MM/YYYY HH:mm')}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card
        title={`Articles dans la correction (${localItems?.length || 0})`}
        style={{ marginTop: 20 }}
        extra={(
          <Space>
            <ExcelDetailImport
              type="correctionstock"
              onImport={handleExcelImport}
              disabled={isValidated}
            />
            <PermissionButton permissionModule="stockCorrections" permissionAction="update"
              onClick={() => setIsAddItemDrawerVisible(true)}
              type="primary"
              disabled={isValidated}
            >
              Ajouter Article
            </PermissionButton>
          </Space>
        )}
      >
        <NestedAchatTable
          items={localItems || []}
          handleSave={handleSave}
          handleDelete={handleDeleteItem}
          validated={isValidated}
          type="correctionstock"
          showStock={false}
          quantitySign={correctionType === 'SORTIE' ? -1 : 1}
        />
      </Card>

      <Drawer
        title={`Ajouter un article à la correction de stock — ${correctionType === 'SORTIE' ? 'Sortie' : 'Entrée'}`}
        width={900}
        onClose={() => setIsAddItemDrawerVisible(false)}
        open={isAddItemDrawerVisible}
      >
        <AddItemToCorrectionStockForm
          refCorrection={localCorrection?.REF_COR}
          onSuccess={(newItems) => {
            const itemsToAdd = Array.isArray(newItems) ? newItems : [newItems];
            itemsToAdd.forEach((newItem) => {
              const existingIndex = localItems.findIndex((item) => item.CODE_ART === newItem.CODE_ART);
              if (existingIndex >= 0) {
                message.warning(`L'article ${newItem.CODE_ART} est déjà dans la correction`);
                return;
              }
              const itemToAdd = {
                CODE_ART: newItem.CODE_ART,
                ARTICLE: newItem.ARTICLE,
                QTE: Math.abs(Number(newItem.QTE)) || 1,
                REMARQUE: newItem.REMARQUE || '',
              };
              setLocalItems((prev) => [...prev, itemToAdd]);
            });
            setHasLocalChanges(true);
            setIsAddItemDrawerVisible(false);
            message.success(itemsToAdd.length > 1 ? `${itemsToAdd.length} articles ajoutés` : 'Article ajouté');
          }}
        />
      </Drawer>
    </Spin>
  );
};

export default SingleCorrectionStock;
